/**
 * Daytona Tracing API
 *
 * Read-only inspection surface for Daytona sandbox traces. Mirrors the LLM
 * tracing UI: list traces with filters → drill into a trace's event timeline →
 * optionally probe the still-alive sandbox for live state (ps/fs/disk).
 *
 * Base path: /api/daytona-tracing
 *
 * Auth: org admin only (OrgAdminGuard reads `org_role` from the Clerk JWT).
 * Single-org scope: every endpoint filters by `@CurrentOrganizationId` from
 * the JWT, mirroring LLM tracing.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";

// ─── Shared primitives ──────────────────────────────────────────
//
// String-literal Zod enums (rather than `z.nativeEnum`) — keeps the inferred
// TypeScript type as `"FOO" | "BAR"` so it is assignable from Prisma's
// generated `$Enums.X` enum members. Backend controllers serialize Prisma
// enum values directly into the response without an extra cast.

const TraceSourceSchema = z.enum(DbEnum.DAYTONA_TRACE_SOURCE_VALUES);
const TraceStatusSchema = z.enum(DbEnum.DAYTONA_TRACE_STATUS_VALUES);
const EventKindSchema = z.enum(DbEnum.DAYTONA_EVENT_KIND_VALUES);
const EventOutcomeSchema = z.enum(DbEnum.DAYTONA_EVENT_OUTCOME_VALUES);

/**
 * DEGRADE, NEVER BLANK — the read side of a database enum.
 *
 * The client parses the WHOLE response in one `.parse()`, so a closed enum on
 * one nested field rejects the entire payload the moment the backend emits a
 * value added after the loaded bundle was built. Frontend (Vercel) and backend
 * (Porter) deploy independently and nothing closes an open tab, so that window
 * is routine rather than theoretical: widening `DaytonaEventKind` used to take
 * the whole trace-detail panel down with a ZodError until the user reloaded.
 *
 * Accepting any string on the response keeps the unknown value confined to its
 * own row, which the display layer renders raw. Same rule as
 * `FeatureFlagOverrideSchema`'s `.catch(null)` and the agent-tool dispatch
 * schema's per-field `.catch(undefined)`.
 *
 * REQUEST params keep the closed enums below: a client sending a value outside
 * the set is a malformed request, not a peer newer than us.
 *
 * The backend still emits only real enum members — its serializers read from
 * views typed `$Enums.DaytonaEventKind`, which is where that guarantee lives.
 */
const readEnum = (schema: z.ZodType<string>) => z.union([schema, z.string()]);

const TraceSourceReadSchema = readEnum(TraceSourceSchema);
const TraceStatusReadSchema = readEnum(TraceStatusSchema);
const EventKindReadSchema = readEnum(EventKindSchema);
const EventOutcomeReadSchema = readEnum(EventOutcomeSchema);

const TraceIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

// ─── List endpoint ──────────────────────────────────────────────

/**
 * Filters mirror the indexed columns + GIN-indexed `context @>` containment
 * keys. Unlisted context keys (nodeType, customerId, …) can be added later
 * without breaking existing callers — the schema is forward-compatible.
 */
export const ListDaytonaTracesParamsSchema = z.object({
  source: TraceSourceSchema.optional(),
  status: TraceStatusSchema.optional(),
  // `z.coerce.boolean()` is unsafe for query strings — `Boolean("false") === true`.
  // Preprocess explicitly so `?hasErrors=false` filters to non-erroring traces.
  hasErrors: z.preprocess(
    (v) => (v === "true" ? true : v === "false" ? false : v),
    z.boolean().optional()
  ),
  sandboxId: z.string().optional(),
  triggeredByUserId: z.string().optional(),
  // Context @> filters — each is a single GIN-indexed containment query.
  conversationId: z.string().optional(),
  workflowId: z.string().uuid().optional(),
  runId: z.string().uuid().optional(),
  nodeId: z.string().optional(),
  agentId: z.string().uuid().optional(),
  chatId: z.string().uuid().optional(),
  // Tag containment — GIN-indexed array overlap.
  tag: z.string().optional(),
  // Time-range filter on startedAt — ISO 8601 strings.
  startedAfter: z.string().datetime().optional(),
  startedBefore: z.string().datetime().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(250).default(50)
});

/** Trimmed view of a trace for the list page — no events, no payloads. */
export const DaytonaTraceListItemSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  source: TraceSourceReadSchema,
  status: TraceStatusReadSchema,
  context: z.record(z.string(), z.unknown()).nullable(),
  tags: z.array(z.string()),
  sandboxId: z.string().nullable(),
  snapshotName: z.string().nullable(),
  target: z.string().nullable(),
  hasErrors: z.boolean(),
  errorCount: z.number().int().nonnegative(),
  errorReason: z.string().nullable(),
  totalDurationMs: z.number().int().nonnegative().nullable(),
  // Allocated sandbox resources + computed cost. Null until the sandbox spec
  // was captured (cost is derived from spec × duration × Daytona rates).
  cpuCores: z.number().int().nonnegative().nullable(),
  ramGiB: z.number().int().nonnegative().nullable(),
  diskGiB: z.number().int().nonnegative().nullable(),
  costInUSDTenThousandths: z.number().int().nonnegative().nullable(),
  // First-class attribution dimensions — null where not derivable at the call site.
  deploymentId: z.string().nullable(),
  deploymentSessionId: z.string().nullable(),
  workflowExecutionId: z.string().nullable(),
  agentId: z.string().nullable(),
  customerId: z.string().nullable(),
  eventCount: z.number().int().nonnegative(),
  triggeredByUserId: z.string().nullable(),
  startedAt: z.string(),
  completedAt: z.string().nullable()
});

export const ListDaytonaTracesResponseSchema = z.object({
  traces: z.array(DaytonaTraceListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  limit: z.number().int().min(1)
});

// ─── Detail endpoint ────────────────────────────────────────────

/** Full event including the JSONB payload — only loaded for the detail view. */
export const DaytonaTraceEventDetailSchema = z.object({
  id: z.string().uuid(),
  kind: EventKindReadSchema,
  outcome: EventOutcomeReadSchema,
  payload: z.unknown(),
  payloadVersion: z.number().int().min(1),
  durationMs: z.number().int().nonnegative().nullable(),
  httpStatusCode: z.number().int().nullable(),
  daytonaRequestId: z.string().nullable(),
  createdAt: z.string()
});

export const DaytonaTraceDetailSchema = DaytonaTraceListItemSchema.extend({
  metadata: z.record(z.string(), z.unknown()).nullable(),
  runnerId: z.string().nullable(),
  events: z.array(DaytonaTraceEventDetailSchema)
});

// ─── Probe endpoint ─────────────────────────────────────────────

/** Single executed playbook command. `error` is set when the SDK call itself threw. */
export const DaytonaProbeCommandResultSchema = z.object({
  label: z.string(),
  command: z.string(),
  exitCode: z.number().int().nullable(),
  stdout: z.string(),
  durationMs: z.number().int().nonnegative(),
  error: z.string().nullable()
});

export const DaytonaProbeResponseSchema = z.object({
  sandboxId: z.string(),
  found: z.boolean(),
  state: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  snapshotName: z.string().nullable(),
  runnerId: z.string().nullable(),
  commands: z.array(DaytonaProbeCommandResultSchema),
  /** Set when `found = false` — the SDK couldn't resolve the sandbox at all. */
  notFoundReason: z.string().nullable()
});

// ─── Endpoint definitions ───────────────────────────────────────

export const ZAdminDaytonaTracing = {
  /**
   * GET /api/daytona-tracing — paginated list with filters.
   * Sorted by startedAt DESC. Filters compose: every supplied key is AND-ed.
   */
  ListTraces: {
    method: "GET" as const,
    path: "/api/daytona-tracing",
    Params: ListDaytonaTracesParamsSchema,
    Response: ApiSuccessSchema(ListDaytonaTracesResponseSchema)
  },

  /**
   * GET /api/daytona-tracing/:id — single trace + full event timeline.
   * Returns 404 when the trace is not in the caller's org.
   */
  GetTrace: {
    method: "GET" as const,
    path: "/api/daytona-tracing/:id",
    PathVars: TraceIdPathVarsSchema,
    Response: ApiSuccessSchema(DaytonaTraceDetailSchema)
  },

  /**
   * POST /api/daytona-tracing/:id/probe — live inspection of the still-alive
   * sandbox via a hard-coded command set (see Inspection Playbook). Hard-coded
   * — never accepts caller-supplied commands — to prevent the probe from being
   * a generic shell into customer sandboxes.
   *
   * If the trace's sandbox has been garbage-collected by Daytona, returns
   * `found: false` rather than 404 — the trace itself still exists, only
   * the live state is gone.
   */
  ProbeSandbox: {
    method: "POST" as const,
    path: "/api/daytona-tracing/:id/probe",
    PathVars: TraceIdPathVarsSchema,
    Body: z.object({}).optional(),
    Response: ApiSuccessSchema(DaytonaProbeResponseSchema)
  }
} as const;

// ─── Convenience type exports ──────────────────────────────────

export type AdminListDaytonaTracesParams = z.infer<typeof ListDaytonaTracesParamsSchema>;
export type AdminListDaytonaTracesResponse = z.infer<typeof ListDaytonaTracesResponseSchema>;
export type AdminDaytonaTraceListItem = z.infer<typeof DaytonaTraceListItemSchema>;
export type AdminDaytonaTraceDetail = z.infer<typeof DaytonaTraceDetailSchema>;
export type AdminDaytonaTraceEventDetail = z.infer<typeof DaytonaTraceEventDetailSchema>;
export type AdminDaytonaProbeResponse = z.infer<typeof DaytonaProbeResponseSchema>;
export type AdminDaytonaProbeCommandResult = z.infer<typeof DaytonaProbeCommandResultSchema>;
